import Phaser from 'phaser';
import { GAME } from '../config';
import { FACTIONS } from '../../shared/factions';
import { loadProgress } from '../../shared/progress';
import type { PlayerProgress } from '../../shared/types';
import {
  BATTLE_CAMPAIGN_STAGES,
  campaignChapters,
  stagesOfChapter,
  type BattleStageDef,
} from '../data/battle-campaign';
import {
  defaultChapterFor,
  isStageUnlocked,
  isStageCleared,
  getStageStars,
} from '../progression/battle-campaign-progress';
import { setActiveCampaignStage } from '../progression/active-campaign-stage';

/**
 * CampaignMapScene — Candy Crush 風線性闖關地圖.
 *
 * 緣由:老闆 2026-06-06「感覺沒有闖關的感覺」→ 拍板做線性關卡地圖 + 過關解鎖.
 * 研究文件:docs/spec/STAGE_PROGRESSION_RESEARCH.md(§4 MVP)。
 *
 * 畫面:關卡圓點排成由左下往右上的彎路 + 連線。
 *   - 已通關:亮燈(門派色)+ 星(1~3,讀 stageStars)。
 *   - 已解鎖未通關:可點(脈動高亮「下一關」)。
 *   - 未解鎖:灰 + 🔒,點了只 shake/toast。
 * 線性解鎖由 battleCampaign.clearedStages 即時推算(isStageUnlocked),**不另存**。
 *
 * 鐵則:本場景只 navigate(點解鎖關 → setActiveCampaignStage + start 戰鬥),
 *   0 題目、0 戰力寫入。返回 hub 用「← 返回」。
 *
 * 入口:HubScene「出戰」→ 本場景;結算勝利 → 回本場景(帶 justClearedStageId
 *   做新解鎖/亮燈動畫)。
 */
export class CampaignMapScene extends Phaser.Scene {
  /** 點關卡的 hit 半徑(px)。圓點視覺半徑 + 觸控餘裕。 */
  private static readonly NODE_RADIUS = 30;

  private progress: PlayerProgress | null = null;
  private toast: Phaser.GameObjects.Text | null = null;
  /** 本次進場前剛通關的關卡 id(由 ResultScene init data 帶入),做亮燈動畫。 */
  private _justClearedStageId: string | null = null;
  /**
   * 目前顯示的章節(R-004 hotfix「地圖壞了」:三章共用同組 mapPos,18 節點
   * 全畫會疊三層 → 一次只畫一章,◀ ▶ 翻章)。0 = create 時自動決定
   * (剛通關 → 該關所屬章;否則 → 進度最前緣章)。
   */
  private _chapter = 0;

  constructor() {
    super({ key: 'CampaignMapScene' });
  }

  init(data?: { justClearedStageId?: string; chapter?: number }): void {
    this._justClearedStageId =
      (data && typeof data.justClearedStageId === 'string' && data.justClearedStageId.length > 0)
        ? data.justClearedStageId
        : null;
    this._chapter =
      (data && typeof data.chapter === 'number' && data.chapter > 0) ? data.chapter : 0;
  }

  /** 把關卡的相對 mapPos(0..1)換成畫布像素(留上下/左右邊距)。 */
  private _nodePixel(def: BattleStageDef): { x: number; y: number } {
    const marginX = 80;
    const marginTop = 72; // 讓出頂部 nav bar
    const marginBottom = 56;
    const w = GAME.width - marginX * 2;
    const h = GAME.height - marginTop - marginBottom;
    return {
      x: marginX + def.mapPos.x * w,
      y: marginTop + def.mapPos.y * h,
    };
  }

  create(): void {
    try { this.progress = loadProgress(); } catch { this.progress = null; }

    // 決定顯示章節:init 指定 > 剛通關的那關所屬章 > 進度最前緣章。
    if (this._chapter <= 0) {
      const justDef = this._justClearedStageId
        ? BATTLE_CAMPAIGN_STAGES.find((s) => s.id === this._justClearedStageId)
        : undefined;
      this._chapter = justDef?.chapter ?? defaultChapterFor(this.progress);
    }

    // 背景 — 夜空冒險地圖風(全幅底色 + 縱向漸層帶 + 星點),對齊 hub 視覺語言,
    // 讓關卡圓點像漂在「夜空地圖」上,而不是浮在純色塊。
    try {
      // 全幅底色(蓋滿 800×600)。
      this.add.rectangle(GAME.width / 2, GAME.height / 2, GAME.width, GAME.height, 0x0e1422)
        .setDepth(-10);
      // 上(冷藍夜空)→ 下(深色地平)三段漸層帶。
      this.add.rectangle(GAME.width / 2, 150, GAME.width, 300, 0x1a2740, 0.5).setDepth(-9);
      this.add.rectangle(GAME.width / 2, 360, GAME.width, 160, 0x162035, 0.45).setDepth(-9);
      this.add.rectangle(GAME.width / 2, GAME.height - 110, GAME.width, 240, 0x10182a, 0.65).setDepth(-9);
    } catch { /* headless */ }
    // 星點(裝飾,靜態 → 不受 reduceMotion 影響)。circle 缺席的精簡 stub 靜默跳過。
    try {
      const stars: Array<[number, number, number]> = [
        [70, 90, 2], [160, 60, 1.5], [300, 100, 2], [430, 70, 1.5],
        [560, 110, 2], [660, 64, 1.5], [730, 130, 2], [120, 180, 1.5],
        [760, 220, 2], [40, 260, 1.5],
      ];
      for (const [sx, sy, sr] of stars) {
        const s = this.add.circle(sx, sy, sr, 0xcfe0ff, 0.45);
        try { s.setDepth(-8); } catch { /* noop */ }
      }
    } catch { /* headless */ }

    // 連線(彎路)— 先畫線,圓點疊在上面。已通關段落用亮色,未達用暗色。
    try { this._drawConnections(); } catch { /* headless */ }

    // 關卡圓點。
    try { this._drawNodes(); } catch { /* headless */ }

    // 頂部 nav。
    try { this._buildTopNav(); } catch { /* headless */ }

    // 剛通關 → 對該節點做一次「亮燈 punch」。
    if (this._justClearedStageId) {
      try { this._celebrateCleared(this._justClearedStageId); } catch { /* headless */ }
    }
  }

  private _drawConnections(): void {
    const stages = stagesOfChapter(this._chapter);
    for (let i = 0; i < stages.length - 1; i++) {
      const a = stages[i];
      const b = stages[i + 1];
      const pa = this._nodePixel(a);
      const pb = this._nodePixel(b);
      // 線段「亮」的條件:起點關已通關(代表這條路已走過)。
      const lit = this.progress ? isStageCleared(this.progress, a.id) : false;
      const color = lit ? 0xffd54f : 0x33425e;
      const alpha = lit ? 0.9 : 0.5;
      try {
        const line = this.add.line(0, 0, pa.x, pa.y, pb.x, pb.y, color, alpha);
        try { line.setOrigin(0, 0); } catch { /* noop */ }
        try { line.setLineWidth?.(lit ? 5 : 3); } catch { /* noop */ }
        try { line.setDepth(-5); } catch { /* noop */ }
      } catch { /* headless */ }
    }
  }

  private _drawNodes(): void {
    for (const def of stagesOfChapter(this._chapter)) {
      const pos = this._nodePixel(def);
      const unlocked = this.progress ? isStageUnlocked(this.progress, def.id) : (def.order === 1);
      const cleared = this.progress ? isStageCleared(this.progress, def.id) : false;
      const stars = this.progress ? getStageStars(this.progress, def.id) : 0;
      const f = FACTIONS[def.hubFactionId];
      const factionColor = f?.colorPrimary ?? 0x4a90d9;

      // 圓點底色:已通關=門派色亮、解鎖未通關=門派色半亮、未解鎖=灰。
      let fill = 0x2a3548;
      let stroke = 0x55617a;
      if (cleared) { fill = factionColor; stroke = 0xffd54f; }
      else if (unlocked) { fill = factionColor; stroke = 0xffffff; }

      // boss 關畫大一點。
      const r = def.isBoss ? CampaignMapScene.NODE_RADIUS + 8 : CampaignMapScene.NODE_RADIUS;

      // 節點光暈 — 解鎖/通關的節點後方加門派色暈光,讓「能打的關」發光,
      //   未解鎖節點不發光(降低存在感)。boss 關用金色暈光更醒目。
      try {
        if (unlocked || cleared) {
          const haloColor = def.isBoss ? 0xffd54f : factionColor;
          const halo = this.add.circle(pos.x, pos.y, r + 12, haloColor, 0.22);
          try { halo.setDepth(1); } catch { /* noop */ }
          // 當前可打關(解鎖未通關)→ 暈光也呼吸,強化「打這關」。
          if (unlocked && !cleared) {
            try {
              this.tweens.add({
                targets: halo, alpha: 0.4, scaleX: 1.25, scaleY: 1.25,
                duration: 800, yoyo: true, repeat: -1, ease: 'Sine.InOut',
              });
            } catch { /* headless */ }
          }
        }
      } catch { /* headless */ }

      // boss 關金色外環 — 讓「章節魔王」一眼就比普通關特別(王冠級目標)。
      if (def.isBoss) {
        try {
          const ring = this.add.circle(pos.x, pos.y, r + 6, 0x000000, 0);
          try { ring.setStrokeStyle?.(3, 0xffd54f, unlocked || cleared ? 0.95 : 0.45); } catch { /* noop */ }
          try { ring.setDepth(2); } catch { /* noop */ }
        } catch { /* headless */ }
      }

      let circle: Phaser.GameObjects.Arc | null = null;
      try {
        circle = this.add.circle(pos.x, pos.y, r, fill, cleared || unlocked ? 1 : 0.6);
        try { circle.setStrokeStyle?.(cleared ? 4 : 3, stroke, 1); } catch { /* noop */ }
        try { circle.setDepth(2); } catch { /* noop */ }
      } catch { /* headless */ }

      // 解鎖未通關 → 脈動高亮(「下一關在這」)。
      if (unlocked && !cleared && circle) {
        try {
          this.tweens.add({
            targets: circle, scaleX: 1.12, scaleY: 1.12,
            duration: 700, yoyo: true, repeat: -1, ease: 'Sine.InOut',
          });
        } catch { /* headless */ }
      }

      // 中央圖示:boss=門派 emoji、普通=order 數字、未解鎖=🔒。
      const icon = !unlocked ? '🔒' : (def.isBoss ? (f?.emoji ?? '👑') : String(def.order));
      try {
        const t = this.add.text(pos.x, pos.y, icon, {
          fontSize: def.isBoss ? '28px' : '22px',
          fontFamily: 'sans-serif',
          color: unlocked ? '#ffffff' : '#9aa3b5',
          fontStyle: 'bold',
        });
        try { t.setOrigin(0.5); } catch { /* noop */ }
        try { t.setDepth(3); } catch { /* noop */ }
      } catch { /* headless */ }

      // 星等(已通關才顯示),圓點上方一排 ★。
      if (cleared) {
        const filled = Math.max(0, Math.min(3, stars));
        const starStr = '★'.repeat(filled) + '☆'.repeat(3 - filled);
        try {
          const st = this.add.text(pos.x, pos.y - r - 12, starStr, {
            fontSize: '16px', fontFamily: 'sans-serif', color: '#ffe082',
          });
          try { st.setOrigin(0.5); } catch { /* noop */ }
          try { st.setDepth(3); } catch { /* noop */ }
        } catch { /* headless */ }
      }

      // 關卡名(圓點下方)。
      try {
        const nameTag = this.add.text(pos.x, pos.y + r + 14, def.displayName, {
          fontSize: '13px', fontFamily: 'sans-serif',
          color: unlocked ? '#e8eef7' : '#7a8499',
        });
        try { nameTag.setOrigin(0.5); } catch { /* noop */ }
        try { nameTag.setDepth(3); } catch { /* noop */ }
      } catch { /* headless */ }

      // 點擊:解鎖 → 進戰鬥;未解鎖 → shake + toast。
      if (circle) {
        try {
          circle.setInteractive({ useHandCursor: true });
          circle.on('pointerdown', () => this.onStageClick(def.id));
        } catch { /* headless */ }
      }
    }
  }

  /**
   * 點關卡節點 — 解鎖 → setActiveCampaignStage + start 戰鬥(帶該關的
   * hubFactionId / stage / bossId);未解鎖 → toast 提示。
   *
   * 鐵則:只 navigate,0 題目。
   */
  onStageClick(stageId: string): void {
    const def = BATTLE_CAMPAIGN_STAGES.find((s) => s.id === stageId);
    if (!def) return;
    const unlocked = this.progress ? isStageUnlocked(this.progress, stageId) : (def.order === 1);
    if (!unlocked) {
      try { this._showToast('先通過前一關才能解鎖!'); } catch { /* noop */ }
      return;
    }

    // 設定「接下來這場屬於哪個闖關關卡」(module flag,result-scene consume)。
    try { setActiveCampaignStage(stageId); } catch { /* noop */ }

    const startBattle = () => {
      // 內部 scene.start 不走 setMode → 補發 ef:enter-battle 重掛技能按鈕。
      try { window.dispatchEvent(new CustomEvent('ef:enter-battle')); } catch { /* noop */ }
      try {
        this.scene.start('HorizontalBattleScene', {
          hubFactionId: def.hubFactionId,
          stage: def.stage,
          ...(def.bossId ? { bossId: def.bossId } : {}),
        });
      } catch { /* noop */ }
    };

    // Fade out → start(對齊 HubScene pattern);無 camera → 直接 start。
    try {
      const cam = this.cameras?.main;
      const fadeOut = (cam as unknown as { fadeOut?: (ms: number, r?: number, g?: number, b?: number) => void })?.fadeOut;
      if (cam && typeof fadeOut === 'function') {
        fadeOut.call(cam, 250, 0, 0, 0);
        const once = (cam as unknown as { once?: (e: string, cb: () => void) => void }).once;
        if (typeof once === 'function') once.call(cam, 'camerafadeoutcomplete', startBattle);
        else startBattle();
      } else {
        startBattle();
      }
    } catch { startBattle(); }
  }

  private _buildTopNav(): void {
    try {
      this.add.rectangle(GAME.width / 2, 24, GAME.width, 48, 0x10131e, 0.9).setOrigin(0.5).setDepth(40);
    } catch { /* headless */ }
    try {
      const back = this.add.text(72, 24, '← 返回', {
        fontSize: '18px', fontFamily: 'sans-serif', color: '#FFD54F',
      });
      try { back.setOrigin(0.5); } catch { /* noop */ }
      try { back.setDepth(41); } catch { /* noop */ }
      try {
        back.setSize(Math.max(back.width, 44), 44);
        back.setInteractive({ useHandCursor: true });
        back.on('pointerdown', () => this.onBack());
      } catch { /* headless */ }
    } catch { /* headless */ }
    try {
      const title = this.add.text(GAME.width / 2, 24, `闖關地圖 — 第 ${this._chapter} 章`, {
        fontSize: '18px', fontFamily: 'sans-serif', color: '#e8eef7', fontStyle: 'bold',
      });
      try { title.setOrigin(0.5); } catch { /* noop */ }
      try { title.setDepth(41); } catch { /* noop */ }
    } catch { /* headless */ }

    // 章節切換 ◀ ▶(R-004 hotfix 分頁)。邊界章節隱藏對應箭頭。
    try {
      const chapters = campaignChapters();
      const idx = chapters.indexOf(this._chapter);
      const mk = (x: number, label: string, target: number) => {
        const btn = this.add.text(x, 24, label, {
          fontSize: '20px', fontFamily: 'sans-serif', color: '#FFD54F', fontStyle: 'bold',
        });
        try { btn.setOrigin(0.5); } catch { /* noop */ }
        try { btn.setDepth(41); } catch { /* noop */ }
        try {
          btn.setSize(Math.max(btn.width, 44), 44);
          btn.setInteractive({ useHandCursor: true });
          btn.on('pointerdown', () => this.switchChapter(target));
        } catch { /* headless */ }
      };
      if (idx > 0) mk(GAME.width / 2 - 130, '◀', chapters[idx - 1]);
      if (idx >= 0 && idx < chapters.length - 1) mk(GAME.width / 2 + 130, '▶', chapters[idx + 1]);
    } catch { /* headless */ }

    // 「🔧 升級基地」入口(boss 2026-06-15)— 右上角金色膠囊鈕,開 MetaShopScene
    // (指揮官基地視覺技能樹)。闖關地圖為唯一主入口 → 入口掛在這最自然。
    // 學習與遊戲分離鐵則:MetaShopScene 只讀寫遊戲軌 metaCoins/metaShop,0 題目。
    try { this._buildMetaShopButton(); } catch { /* headless */ }
  }

  /**
   * 右上角「🔧 升級基地」膠囊鈕 → start MetaShopScene(returnScene 設回本場景,
   * 從基地「‹ 返回」會回到闖關地圖)。金色高亮,跟 nav 既有 #FFD54F 一致。
   */
  private _buildMetaShopButton(): void {
    const w = 116;
    const h = 36;
    const cx = GAME.width - w / 2 - 14;
    const cy = 24;
    try {
      const g = this.add.graphics();
      g.fillStyle(0xf5b53d, 1);
      g.fillRoundedRect(cx - w / 2, cy - h / 2, w, h, 18);
      g.lineStyle(2, 0xfff3d6, 0.9);
      g.strokeRoundedRect(cx - w / 2, cy - h / 2, w, h, 18);
      try { g.setDepth(41); } catch { /* noop */ }
    } catch { /* headless */ }
    try {
      const t = this.add.text(cx, cy, '🔧 升級基地', {
        fontSize: '15px', fontFamily: 'sans-serif', color: '#1a0d2e', fontStyle: 'bold',
      });
      try { t.setOrigin(0.5); } catch { /* noop */ }
      try { t.setDepth(42); } catch { /* noop */ }
    } catch { /* headless */ }
    try {
      const hit = this.add.zone(cx, cy, w, h).setOrigin(0.5);
      try { hit.setDepth(42); } catch { /* noop */ }
      hit.setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => this.onOpenMetaShop());
    } catch { /* headless */ }
  }

  /**
   * 開「指揮官基地」永久升級商店。fade → start('MetaShopScene')，
   * returnScene 帶回本場景。只 navigate,0 題目(學習與遊戲分離鐵則)。
   */
  onOpenMetaShop(): void {
    const go = () => {
      try {
        this.scene.start('MetaShopScene', { returnScene: 'CampaignMapScene' });
      } catch { /* noop */ }
    };
    try {
      const cam = this.cameras?.main;
      const fadeOut = (cam as unknown as { fadeOut?: (ms: number, r?: number, g?: number, b?: number) => void })?.fadeOut;
      if (cam && typeof fadeOut === 'function') {
        fadeOut.call(cam, 220, 0, 0, 0);
        const once = (cam as unknown as { once?: (e: string, cb: () => void) => void }).once;
        if (typeof once === 'function') once.call(cam, 'camerafadeoutcomplete', go);
        else go();
      } else { go(); }
    } catch { go(); }
  }

  /** 翻章:restart 本場景帶 chapter(整頁重畫,沿用同一條 mapPos 彎路)。 */
  switchChapter(chapter: number): void {
    if (!campaignChapters().includes(chapter)) return;
    try { this.scene.restart({ chapter }); } catch { /* headless */ }
  }

  /**
   * 返回首頁。IA 改版(boss 2026-06-14):闖關地圖為唯一主入口,「← 返回」回 home
   * (dispatch 'ef:return-home' → main.ts setMode('home')),不再回已停用的選門大廳。
   */
  onBack(): void {
    const go = () => { try { window.dispatchEvent(new Event('ef:return-home')); } catch { /* noop */ } };
    try {
      const cam = this.cameras?.main;
      const fadeOut = (cam as unknown as { fadeOut?: (ms: number, r?: number, g?: number, b?: number) => void })?.fadeOut;
      if (cam && typeof fadeOut === 'function') {
        fadeOut.call(cam, 250, 0, 0, 0);
        const once = (cam as unknown as { once?: (e: string, cb: () => void) => void }).once;
        if (typeof once === 'function') once.call(cam, 'camerafadeoutcomplete', go);
        else go();
      } else { go(); }
    } catch { go(); }
  }

  private _celebrateCleared(stageId: string): void {
    const def = BATTLE_CAMPAIGN_STAGES.find((s) => s.id === stageId);
    if (!def) return;
    const pos = this._nodePixel(def);
    try {
      const ring = this.add.circle(pos.x, pos.y, CampaignMapScene.NODE_RADIUS, 0xffe082, 0.4);
      try { ring.setDepth(5); } catch { /* noop */ }
      this.tweens.add({
        targets: ring, scaleX: 2.4, scaleY: 2.4, alpha: 0,
        duration: 700, ease: 'Cubic.easeOut',
        onComplete: () => { try { ring.destroy(); } catch { /* noop */ } },
      });
    } catch { /* headless */ }
  }

  private _showToast(msg: string): void {
    try { this.toast?.destroy(); } catch { /* noop */ }
    try {
      this.toast = this.add.text(GAME.width / 2, GAME.height - 28, msg, {
        fontSize: '15px', fontFamily: 'sans-serif', color: '#ffffff',
        backgroundColor: '#7a1c1c', padding: { x: 10, y: 6 },
      });
      try { this.toast.setOrigin(0.5); } catch { /* noop */ }
      try { this.toast.setDepth(100); } catch { /* noop */ }
      try {
        this.tweens.add({
          targets: this.toast, alpha: 0, duration: 1800,
          onComplete: () => { try { this.toast?.destroy(); } catch { /* noop */ } this.toast = null; },
        });
      } catch { /* headless */ }
    } catch { /* headless */ }
  }
}
